import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { MapPin, Mail, Phone, MessageCircle, ArrowRight, Upload, CheckCircle2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { ScrollReveal } from '../components/ScrollReveal';
import { NextPageLink } from '../components/NextPageLink';
import { useSEO } from '../utils/seo';
const LeafletMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      scrollWheelZoom: false,
      zoomControl: true
    }).setView([46.0986, 7.0728], 16);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    }).addTo(map);
    const icon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="width:24px;height:24px;background:#f5f5f0;border-radius:50%;border:3px solid #333;box-shadow:0 2px 8px rgba(0,0,0,0.4);"></div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
    L.marker([46.0986, 7.0728], {
      icon
    }).addTo(map).bindPopup('<strong>Dispel Your Tattoo</strong><br/>Rue de la Délèze 2<br/>1920 Martigny');
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  return <div ref={mapRef} className="w-full h-full min-h-[280px]" data-id="element-398" />;
};
export const Contact = () => {
  useSEO({
    title: 'Contact & Consultation | Détatouage Laser Martigny | Dispel Your Tattoo',
    description: 'Prenez rendez-vous pour une consultation de détatouage laser à Martigny. Formulaire en ligne, carte interactive et coordonnées. Réponse rapide garantie.',
    canonical: 'https://www.dispelyourtattoo.ch/contact'
  });
  // Main Form State
  const [step, setStep] = useState(1);
  const [mainForm, setMainForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    ageTatouage: '',
    taille: '',
    encreBlanche: '',
    objectif: '',
    disponibilites: {
      lundi: false,
      mardi: false,
      mercredi: false,
      jeudi: false,
      vendredi: false,
      samedi: false
    },
    message: '',
    photo: null as File | null
  });
  const [mainFormConsent, setMainFormConsent] = useState(false);
  const [mainFormSubmitted, setMainFormSubmitted] = useState(false);
  // Secondary Form State
  const [shortForm, setShortForm] = useState({
    nom: '',
    email: '',
    message: ''
  });
  const [shortFormConsent, setShortFormConsent] = useState(false);
  const [shortFormSubmitted, setShortFormSubmitted] = useState(false);
  // Parallax refs
  const primaryFormRef = useRef<HTMLElement>(null);
  const secondaryFormRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress: primaryProgress
  } = useScroll({
    target: primaryFormRef,
    offset: ['start end', 'end start']
  });
  const primaryBgY = useTransform(primaryProgress, [0, 1], ['-8%', '8%']);
  const {
    scrollYProgress: secondaryProgress
  } = useScroll({
    target: secondaryFormRef,
    offset: ['start end', 'end start']
  });
  const secondaryBgY = useTransform(secondaryProgress, [0, 1], ['-8%', '8%']);
  const handleMainSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 3) {
      setStep(step + 1);
    } else {
      if (!mainFormConsent) return;
      setMainFormSubmitted(true);
    }
  };
  const handleShortSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shortFormConsent) return;
    setShortFormSubmitted(true);
  };
  return <main className="pt-32 pb-0" data-id="element-399">
      {/* Header Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 max-w-[1100px] mx-auto text-center" data-id="element-400">
        <ScrollReveal data-id="element-401">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-medium text-brand-light mb-8 tracking-tight text-balance" data-id="element-402">
            Contact & Consultation
          </h1>
        </ScrollReveal>
        <ScrollReveal delay={0.1} data-id="element-403">
          <p className="text-lg md:text-xl text-brand-gray font-light leading-relaxed max-w-3xl mx-auto" data-id="element-404">
            Vous souhaitez en savoir plus sur le détatouage ou obtenir une
            évaluation personnalisée ?
            <br className="hidden md:block" data-id="element-405" />
            Je vous accompagne avec transparence, précision et professionnalisme
            à chaque étape de votre projet.
          </p>
        </ScrollReveal>
      </section>

      {/* Primary Consultation Form */}
      <section ref={primaryFormRef} id="consultation-form" className="relative py-16 md:py-24 border-t border-brand-border/30 scroll-mt-24 overflow-hidden" data-id="element-406">
        {/* Parallax Background */}
        <div className="absolute inset-0 z-0" data-id="element-407">
          <motion.div style={{
          y: primaryBgY
        }} className="absolute -top-[15%] left-0 w-full h-[130%]" data-id="element-408">
            <img src="https://images.unsplash.com/photo-1611501275019-9b5cda994e8d?w=1920&q=80" alt="" className="w-full h-full object-cover opacity-60 grayscale" data-id="element-409" />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-b from-brand-darker/40 via-brand-darker/30 to-brand-darker/40" data-id="element-410" />
        </div>

        <div className="relative z-10 max-w-[800px] mx-auto px-6 md:px-12" data-id="element-411">
          <ScrollReveal data-id="element-412">
            <div className="text-center mb-12" data-id="element-413">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-light mb-4 tracking-tight" data-id="element-414">
                Demande de devis sans engagement
              </h2>
              <p className="text-brand-gray font-light" data-id="element-415">
                Réponse rapide — cela prend moins d'une minute pour commencer.
                <br data-id="element-416" />
                Ajoutez des détails pour une estimation plus précise
                (optionnel).
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1} data-id="element-417">
            <div className="bg-brand-dark/85 backdrop-blur-md border border-brand-border/50 rounded-3xl p-8 md:p-12 relative overflow-hidden" data-id="element-418">
              {/* Progress Bar */}
              {!mainFormSubmitted && <div className="absolute top-0 left-0 w-full h-1 bg-brand-darker" data-id="element-419">
                  <motion.div className="h-full bg-brand-light" initial={{
                width: '33%'
              }} animate={{
                width: `${step / 3 * 100}%`
              }} transition={{
                duration: 0.5,
                ease: 'easeInOut'
              }} data-id="element-420" />
                </div>}

              <AnimatePresence mode="wait" data-id="element-421">
                {mainFormSubmitted ? <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="text-center py-12" data-id="element-422">
                    <CheckCircle2 className="w-16 h-16 text-brand-light mx-auto mb-6" data-id="element-423" />
                    <h3 className="text-2xl font-bold text-brand-light mb-4" data-id="element-424">
                      Demande envoyée
                    </h3>
                    <p className="text-brand-gray font-light" data-id="element-425">
                      Si vous n'avez pas reçu de réponse sous deux semaines,
                      n'hésitez pas à me contacter sur WhatsApp : il est
                      possible que notre réponse soit arrivée dans vos spams.
                    </p>
                  </motion.div> : <motion.form key={`step-${step}`} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.3
              }} onSubmit={handleMainSubmit} className="space-y-8" data-id="element-426">
                    {/* Step 1: Essential */}
                    {step === 1 && <div className="space-y-6" data-id="element-427">
                        <div className="flex items-center justify-between mb-8" data-id="element-428">
                          <h3 className="text-xl font-medium text-brand-light" data-id="element-429">
                            1. Vos coordonnées
                          </h3>
                          <span className="text-sm text-brand-gray" data-id="element-430">
                            Étape 1 sur 3
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-431">
                          <div className="space-y-2" data-id="element-432">
                            <label className="text-sm font-medium text-brand-gray" data-id="element-433">
                              Prénom *
                            </label>
                            <input required type="text" value={mainForm.prenom} onChange={e => setMainForm({
                        ...mainForm,
                        prenom: e.target.value
                      })} className="w-full bg-transparent border-b border-brand-border/50 py-3 text-brand-light focus:outline-none focus:border-brand-light transition-colors" data-id="element-434" />
                          </div>
                          <div className="space-y-2" data-id="element-435">
                            <label className="text-sm font-medium text-brand-gray" data-id="element-436">
                              Nom *
                            </label>
                            <input required type="text" value={mainForm.nom} onChange={e => setMainForm({
                        ...mainForm,
                        nom: e.target.value
                      })} className="w-full bg-transparent border-b border-brand-border/50 py-3 text-brand-light focus:outline-none focus:border-brand-light transition-colors" data-id="element-437" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-438">
                          <div className="space-y-2" data-id="element-439">
                            <label className="text-sm font-medium text-brand-gray" data-id="element-440">
                              Email *
                            </label>
                            <input required type="email" value={mainForm.email} onChange={e => setMainForm({
                        ...mainForm,
                        email: e.target.value
                      })} className="w-full bg-transparent border-b border-brand-border/50 py-3 text-brand-light focus:outline-none focus:border-brand-light transition-colors" data-id="element-441" />
                          </div>
                          <div className="space-y-2" data-id="element-442">
                            <label className="text-sm font-medium text-brand-gray" data-id="element-443">
                              Téléphone *
                            </label>
                            <input required type="tel" value={mainForm.telephone} onChange={e => setMainForm({
                        ...mainForm,
                        telephone: e.target.value
                      })} className="w-full bg-transparent border-b border-brand-border/50 py-3 text-brand-light focus:outline-none focus:border-brand-light transition-colors" data-id="element-444" />
                          </div>
                        </div>
                      </div>}

                    {/* Step 2: Tattoo Details */}
                    {step === 2 && <div className="space-y-8" data-id="element-445">
                        <div className="flex items-center justify-between mb-2" data-id="element-446">
                          <h3 className="text-xl font-medium text-brand-light" data-id="element-447">
                            2. Détails du tatouage
                          </h3>
                          <span className="text-sm text-brand-gray" data-id="element-448">
                            Étape 2 sur 3
                          </span>
                        </div>

                        <div className="space-y-2" data-id="element-449">
                          <label className="text-sm font-medium text-brand-gray" data-id="element-450">
                            Quel âge a environ ton tatouage ?
                          </label>
                          <input type="text" value={mainForm.ageTatouage} onChange={e => setMainForm({
                      ...mainForm,
                      ageTatouage: e.target.value
                    })} className="w-full bg-transparent border-b border-brand-border/50 py-3 text-brand-light focus:outline-none focus:border-brand-light transition-colors" data-id="element-451" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-452">
                          <div className="space-y-2" data-id="element-453">
                            <label className="text-sm font-medium text-brand-gray" data-id="element-454">
                              Quelle est la taille du tatouage ?
                            </label>
                            <select value={mainForm.taille} onChange={e => setMainForm({
                        ...mainForm,
                        taille: e.target.value
                      })} className="w-full bg-transparent border-b border-brand-border/50 py-3 text-brand-light focus:outline-none focus:border-brand-light transition-colors appearance-none" data-id="element-455">
                              <option value="" className="bg-brand-dark" data-id="element-456">
                                Sélectionner...
                              </option>
                              <option value="petit" className="bg-brand-dark" data-id="element-457">
                                Petit (moins de 5cm)
                              </option>
                              <option value="moyen" className="bg-brand-dark" data-id="element-458">
                                Moyen (5 à 15cm)
                              </option>
                              <option value="grand" className="bg-brand-dark" data-id="element-459">
                                Grand (plus de 15cm)
                              </option>
                            </select>
                          </div>
                        </div>

                        <div className="space-y-4" data-id="element-460">
                          <label className="text-sm font-medium text-brand-gray block" data-id="element-461">
                            Le tatoueur a-t-il utilisé de l'encre blanche dans
                            votre tatouage (reflets ou couleur) ?
                          </label>
                          <div className="flex flex-wrap gap-6" data-id="element-462">
                            {['Oui', 'Non', 'Incertain'].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer group" data-id="element-463">
                                <div className="relative flex items-center justify-center w-5 h-5" data-id="element-464">
                                  <input type="radio" name="encreBlanche" value={opt} checked={mainForm.encreBlanche === opt} onChange={e => setMainForm({
                            ...mainForm,
                            encreBlanche: e.target.value
                          })} className="peer appearance-none w-5 h-5 rounded-full border border-brand-border/80 checked:border-brand-light transition-colors cursor-pointer" data-id="element-465" />
                                  <div className="absolute w-2.5 h-2.5 rounded-full bg-brand-light opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" data-id="element-466" />
                                </div>
                                <span className="text-brand-light group-hover:text-white transition-colors" data-id="element-467">
                                  {opt}
                                </span>
                              </label>)}
                          </div>
                        </div>

                        <div className="space-y-4" data-id="element-468">
                          <label className="text-sm font-medium text-brand-gray block" data-id="element-469">
                            Souhaitez-vous un retrait complet ou une atténuation
                            pour un cover-up ?
                          </label>
                          <div className="flex flex-col space-y-3" data-id="element-470">
                            {['Retrait complet', 'Atténuation pour cover-up'].map(opt => <label key={opt} className="flex items-center space-x-3 cursor-pointer group" data-id="element-471">
                                <div className="relative flex items-center justify-center w-5 h-5" data-id="element-472">
                                  <input type="radio" name="objectif" value={opt} checked={mainForm.objectif === opt} onChange={e => setMainForm({
                            ...mainForm,
                            objectif: e.target.value
                          })} className="peer appearance-none w-5 h-5 rounded-full border border-brand-border/80 checked:border-brand-light transition-colors cursor-pointer" data-id="element-473" />
                                  <div className="absolute w-2.5 h-2.5 rounded-full bg-brand-light opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" data-id="element-474" />
                                </div>
                                <span className="text-brand-light group-hover:text-white transition-colors" data-id="element-475">
                                  {opt}
                                </span>
                              </label>)}
                          </div>
                        </div>

                        <div className="space-y-2 pt-4" data-id="element-476">
                          <label className="text-sm font-medium text-brand-gray block mb-4" data-id="element-477">
                            Photo(s) du tatouage à retirer (max 10)
                          </label>
                          <div className="border border-dashed border-brand-border/50 rounded-xl p-6 md:p-8 text-center hover:border-brand-light/50 transition-colors cursor-pointer group" data-id="element-478">
                            <input type="file" className="hidden" id="photo-upload" accept="image/*" multiple onChange={e => setMainForm({
                        ...mainForm,
                        photo: e.target.files?.[0] || null
                      })} data-id="element-479" />
                            <label htmlFor="photo-upload" className="cursor-pointer flex flex-col items-center" data-id="element-480">
                              <Upload className="w-8 h-8 text-brand-gray group-hover:text-brand-light transition-colors mb-3" data-id="element-481" />
                              <span className="text-sm text-brand-gray group-hover:text-brand-light transition-colors break-all px-2" data-id="element-482">
                                {mainForm.photo ? mainForm.photo.name : 'Cliquez pour ajouter une photo'}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>}

                    {/* Step 3: Additional Info */}
                    {step === 3 && <div className="space-y-8" data-id="element-483">
                        <div className="flex items-center justify-between mb-2" data-id="element-484">
                          <h3 className="text-xl font-medium text-brand-light" data-id="element-485">
                            3. Disponibilités & informations
                          </h3>
                          <span className="text-sm text-brand-gray" data-id="element-486">
                            Étape 3 sur 3
                          </span>
                        </div>

                        <div className="space-y-4" data-id="element-487">
                          <label className="text-sm font-medium text-brand-gray block" data-id="element-488">
                            Quels jours êtes-vous disponible pour les
                            rendez-vous ?
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-489">
                            {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'].map(jour => {
                        const key = jour.toLowerCase() as keyof typeof mainForm.disponibilites;
                        return <label key={jour} className="flex items-center space-x-3 cursor-pointer group" data-id="element-490">
                                  <input type="checkbox" checked={mainForm.disponibilites[key]} onChange={e => setMainForm({
                            ...mainForm,
                            disponibilites: {
                              ...mainForm.disponibilites,
                              [key]: e.target.checked
                            }
                          })} className="w-4 h-4 rounded border-brand-border/80 bg-transparent accent-brand-light shrink-0 cursor-pointer" data-id="element-491" />
                                  <span className="text-brand-light group-hover:text-white transition-colors" data-id="element-492">
                                    {jour}
                                  </span>
                                </label>;
                      })}
                          </div>
                        </div>

                        <div className="space-y-2" data-id="element-493">
                          <label className="text-sm font-medium text-brand-gray" data-id="element-494">
                            Questions, préoccupations ou informations
                            complémentaires
                          </label>
                          <textarea rows={4} placeholder="Faites-nous part de vos questions, préoccupations ou toute information pertinente concernant votre projet de détatouage." value={mainForm.message} onChange={e => setMainForm({
                      ...mainForm,
                      message: e.target.value
                    })} className="w-full bg-transparent border-b border-brand-border/50 py-3 text-brand-light focus:outline-none focus:border-brand-light transition-colors resize-none" data-id="element-495" />
                        </div>

                        {/* Consent Checkbox */}
                        <label className="flex items-start space-x-3 pt-4 cursor-pointer" data-id="element-496">
                          <input type="checkbox" checked={mainFormConsent} onChange={e => setMainFormConsent(e.target.checked)} className="mt-1 w-4 h-4 rounded border-brand-border bg-transparent accent-brand-light shrink-0" required data-id="element-497" />
                          <span className="text-sm text-brand-gray font-light leading-relaxed" data-id="element-498">
                            J'accepte les{' '}
                            <Link to="/mentions-legales" className="underline hover:text-brand-light transition-colors" data-id="element-499">
                              mentions légales
                            </Link>{' '}
                            et la{' '}
                            <Link to="/politique-de-confidentialite" className="underline hover:text-brand-light transition-colors" data-id="element-500">
                              politique de confidentialité
                            </Link>
                            .
                          </span>
                        </label>
                      </div>}

                    <div className="pt-8 flex flex-col-reverse sm:flex-row items-center justify-between gap-4 sm:gap-0" data-id="element-501">
                      {step > 1 ? <button type="button" onClick={() => setStep(step - 1)} className="w-full sm:w-auto text-sm font-medium text-brand-gray hover:text-brand-light transition-colors py-3 sm:py-0" data-id="element-502">
                          Retour
                        </button> : <div className="hidden sm:block" data-id="element-503"></div>}
                      <button type="submit" className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 px-8 py-4 bg-brand-light text-brand-dark rounded-full font-bold uppercase tracking-wider hover:bg-white transition-colors duration-300" data-id="element-504">
                        <span data-id="element-505">
                          {step === 3 ? 'Envoyer la demande' : 'Continuer'}
                        </span>
                        <ArrowRight size={16} className="md:w-[18px] md:h-[18px]" data-id="element-506" />
                      </button>
                    </div>
                  </motion.form>}
              </AnimatePresence>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Info Grid Section */}
      <section className="py-16 md:py-24 bg-brand-dark" data-id="element-507">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12" data-id="element-508">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-509">
            {/* Location */}
            <ScrollReveal data-id="element-510">
              <div data-id="element-511">
                <h2 className="text-2xl font-medium text-brand-light mb-8 tracking-tight" data-id="element-512">
                  Nous trouver à Martigny
                </h2>
                <div className="bg-brand-darker border border-brand-border/50 rounded-3xl overflow-hidden h-full flex flex-col" data-id="element-513">
                  <div className="flex items-start space-x-4 p-8 pb-6" data-id="element-514">
                    <MapPin className="w-6 h-6 text-brand-light shrink-0 mt-1" data-id="element-515" />
                    <div data-id="element-516">
                      <p className="font-medium text-brand-light mb-1" data-id="element-517">
                        Dispel Your Tattoo — Détatouage
                      </p>
                      <p className="text-brand-gray font-light leading-relaxed" data-id="element-518">
                        Rue de la Délèze 2<br data-id="element-519" />
                        1920 Martigny, Suisse
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 px-6 md:px-8 pb-6" data-id="element-520">
                    <Clock className="w-6 h-6 text-brand-light shrink-0 mt-1" data-id="element-521" />
                    <div data-id="element-522">
                      <p className="font-medium text-brand-light mb-1" data-id="element-523">
                        Horaires
                      </p>
                      <p className="text-brand-gray font-light leading-relaxed" data-id="element-524">
                        Lundi – Vendredi : 09:00 – 18:00
                        <br data-id="element-525" />
                        Sur rendez-vous uniquement
                      </p>
                    </div>
                  </div>
                  <div className="flex-grow rounded-b-3xl overflow-hidden min-h-[250px] md:min-h-[280px]" data-id="element-526">
                    <LeafletMap data-id="element-527" />
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Contact Info */}
            <ScrollReveal delay={0.1} data-id="element-528">
              <div className="h-full flex flex-col" data-id="element-529">
                <h2 className="text-2xl font-medium text-brand-light mb-8 tracking-tight" data-id="element-530">
                  Coordonnées
                </h2>
                <div className="space-y-4 md:space-y-6 flex-grow" data-id="element-531">
                  <a href="mailto:dispeltattoo@gmail.com" className="flex items-center space-x-4 md:space-x-6 p-5 md:p-6 rounded-2xl border border-brand-border/50 hover:border-brand-light/50 hover:bg-brand-darker transition-all group" data-id="element-532">
                    <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full bg-brand-darker border border-brand-border flex items-center justify-center group-hover:border-brand-light/50 transition-colors" data-id="element-533">
                      <Mail className="w-4 h-4 md:w-5 md:h-5 text-brand-light" data-id="element-534" />
                    </div>
                    <div className="min-w-0" data-id="element-535">
                      <p className="text-sm font-medium text-brand-gray mb-1" data-id="element-536">
                        Email
                      </p>
                      <p className="text-brand-light truncate" data-id="element-537">
                        dispeltattoo@gmail.com
                      </p>
                    </div>
                  </a>

                  <a href="tel:+41791026945" className="flex items-center space-x-4 md:space-x-6 p-5 md:p-6 rounded-2xl border border-brand-border/50 hover:border-brand-light/50 hover:bg-brand-darker transition-all group" data-id="element-538">
                    <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full bg-brand-darker border border-brand-border flex items-center justify-center group-hover:border-brand-light/50 transition-colors" data-id="element-539">
                      <Phone className="w-4 h-4 md:w-5 md:h-5 text-brand-light" data-id="element-540" />
                    </div>
                    <div data-id="element-541">
                      <p className="text-sm font-medium text-brand-gray mb-1" data-id="element-542">
                        Téléphone
                      </p>
                      <p className="text-brand-light" data-id="element-543">079 102 69 45</p>
                    </div>
                  </a>

                  <a href="https://wa.me/41791026945" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-4 md:space-x-6 p-5 md:p-6 rounded-2xl border border-brand-border/50 hover:border-brand-light/50 hover:bg-brand-darker transition-all group" data-id="element-544">
                    <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full bg-brand-darker border border-brand-border flex items-center justify-center group-hover:border-brand-light/50 transition-colors" data-id="element-545">
                      <MessageCircle className="w-4 h-4 md:w-5 md:h-5 text-brand-light" data-id="element-546" />
                    </div>
                    <div data-id="element-547">
                      <p className="text-sm font-medium text-brand-gray mb-1" data-id="element-548">
                        WhatsApp
                      </p>
                      <p className="text-brand-light" data-id="element-549">079 102 69 45</p>
                    </div>
                  </a>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Secondary Short Form */}
      <section ref={secondaryFormRef} id="quick-contact" className="relative py-16 md:py-24 border-y border-brand-border/30 scroll-mt-24 overflow-hidden" data-id="element-550">
        {/* Parallax Background */}
        <div className="absolute inset-0 z-0" data-id="element-551">
          <motion.div style={{
          y: secondaryBgY
        }} className="absolute -top-[15%] left-0 w-full h-[130%]" data-id="element-552">
            <img src="https://images.unsplash.com/photo-1565058379802-bbe93b2f703a?w=1920&q=80" alt="" className="w-full h-full object-cover opacity-50 grayscale" loading="lazy" data-id="element-553" />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-b from-brand-darker/70 via-brand-darker/60 to-brand-darker/70" data-id="element-554" />
        </div>

        <div className="relative z-10 max-w-[600px] mx-auto px-6 md:px-12" data-id="element-555">
          <ScrollReveal data-id="element-556">
            <div className="text-center mb-12" data-id="element-557">
              <h2 className="text-2xl font-bold text-brand-light mb-4 tracking-tight" data-id="element-558">
                Une question rapide ?
              </h2>
              <p className="text-brand-gray font-light" data-id="element-559">
                Vous pouvez également nous envoyer un message simple. Nous vous
                répondrons dans les plus brefs délais.
              </p>
            </div>

            {shortFormSubmitted ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-center py-8 border border-brand-border/50 rounded-2xl" data-id="element-560">
                <p className="text-brand-light font-medium" data-id="element-561">
                  Message envoyé avec succès.
                </p>
              </motion.div> : <form onSubmit={handleShortSubmit} className="space-y-6" data-id="element-562">
                <input required type="text" placeholder="Nom *" value={shortForm.nom} onChange={e => setShortForm({
              ...shortForm,
              nom: e.target.value
            })} className="w-full bg-brand-dark/80 backdrop-blur-sm border border-brand-border/50 rounded-xl px-6 py-4 text-brand-light focus:outline-none focus:border-brand-light/50 transition-colors" data-id="element-563" />
                <input required type="email" placeholder="Email *" value={shortForm.email} onChange={e => setShortForm({
              ...shortForm,
              email: e.target.value
            })} className="w-full bg-brand-dark/80 backdrop-blur-sm border border-brand-border/50 rounded-xl px-6 py-4 text-brand-light focus:outline-none focus:border-brand-light/50 transition-colors" data-id="element-564" />
                <textarea placeholder="Message (optionnel)" rows={4} value={shortForm.message} onChange={e => setShortForm({
              ...shortForm,
              message: e.target.value
            })} className="w-full bg-brand-dark/80 backdrop-blur-sm border border-brand-border/50 rounded-xl px-6 py-4 text-brand-light focus:outline-none focus:border-brand-light/50 transition-colors resize-none" data-id="element-565" />

                {/* Consent Checkbox */}
                <label className="flex items-start space-x-3 cursor-pointer" data-id="element-566">
                  <input type="checkbox" checked={shortFormConsent} onChange={e => setShortFormConsent(e.target.checked)} className="mt-1 w-4 h-4 rounded border-brand-border bg-transparent accent-brand-light shrink-0" required data-id="element-567" />
                  <span className="text-sm text-brand-gray font-light leading-relaxed" data-id="element-568">
                    J'accepte les{' '}
                    <Link to="/mentions-legales" className="underline hover:text-brand-light transition-colors" data-id="element-569">
                      mentions légales
                    </Link>{' '}
                    et la{' '}
                    <Link to="/politique-de-confidentialite" className="underline hover:text-brand-light transition-colors" data-id="element-570">
                      politique de confidentialité
                    </Link>
                    .
                  </span>
                </label>

                <button type="submit" className="w-full inline-flex items-center justify-center space-x-2 px-8 py-4 border border-brand-border text-brand-light rounded-full font-bold uppercase tracking-wider hover:border-brand-light transition-colors duration-300" data-id="element-571">
                  <span data-id="element-572">Envoyer le message</span>
                  <ArrowRight size={16} className="md:w-[18px] md:h-[18px]" data-id="element-573" />
                </button>
              </form>}
          </ScrollReveal>
        </div>
      </section>

      {/* About Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-id="element-574">
        {/* Dark Background */}
        <div className="absolute inset-0 z-0 bg-brand-darker" data-id="element-575" />

        <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-12" data-id="element-576">
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-12 lg:gap-16 items-start" data-id="element-577">
            {/* Portrait */}
            <ScrollReveal data-id="element-578">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] max-w-[320px] mx-auto lg:mx-0" data-id="element-579">
                <img src="https://cdn.magicpatterns.com/uploads/cN5WJ8bzYHjA3bYr6hzxnx/IMG_2766-PROOF-THOMAS-MASOTTI.webp" alt="Marc Freiburghaus — Spécialiste en détatouage laser" className="w-full h-full object-cover object-top" data-id="element-580" />
                <div className="absolute inset-0 bg-black/20" data-id="element-581" />
              </div>
            </ScrollReveal>

            {/* Text */}
            <ScrollReveal delay={0.1} data-id="element-582">
              <div data-id="element-583">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-light mb-8 tracking-tight" data-id="element-584">
                  Marc Freiburghaus
                </h2>
                <div className="space-y-5 text-brand-gray font-light leading-relaxed text-base md:text-lg" data-id="element-585">
                  <p data-id="element-586">
                    Je suis spécialisé exclusivement dans le détatouage laser,
                    un domaine que je pratique avec passion depuis maintenant 4
                    ans. Mon activité est entièrement consacrée à l'effacement
                    de tatouages, ce qui me permet de vous offrir une expertise
                    pointue et constamment mise à jour.
                  </p>
                  <p className="text-brand-light font-medium" data-id="element-587">
                    Mon objectif : vous accompagner vers un résultat sûr,
                    maîtrisé et adapté à votre peau.
                  </p>
                  <p data-id="element-588">
                    J'utilise les deux technologies laser les plus reconnues
                    dans la profession — picoseconde et Q-Switched (nanoseconde)
                    — afin d'optimiser le traitement selon chaque type d'encre
                    et chaque tatouage. Les séances se déroulent dans un
                    environnement strictement hygiénique, avec une attention
                    particulière portée à votre sécurité et à votre confort.
                  </p>
                  <p data-id="element-589">
                    Je me ferai un plaisir de vous accueillir, d'évaluer votre
                    projet et de répondre à toutes vos questions avec
                    transparence et professionnalisme.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </main>;
};